"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { useState, useEffect } from "react"
import { createClient } from "@/lib/supabase/client"
import { Check, Users, TrendingUp, Sparkles, ArrowRight } from "lucide-react"
import { useRouter } from "next/navigation"
import Image from "next/image"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"

export function LandingHero() {
  const { isMobile } = usePlatform()
  const [email, setEmail] = useState("")
  const [totalMembers, setTotalMembers] = useState<number | null>(null)
  const [monthlyMembers, setMonthlyMembers] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [mounted, setMounted] = useState(false)
  const router = useRouter()
  
  useEffect(() => {
    setMounted(true)
    fetchMemberStats()
    fetchPexelsVideo()
  }, [])

  const fetchPexelsVideo = async () => {
    try {
      const response = await fetch(`/api/pexels/videos?query=surfing%20waves&per_page=5`)
      const data = await response.json()
      if (data.videos && data.videos.length > 0) {
        const video = data.videos[0]
        const videoFile = video.video_files.find((file: any) => file.quality === "hd" && file.width === 1920) || video.video_files[0]
        setVideoUrl(videoFile.link)
      } else {
        setVideoUrl("https://videos.pexels.com/video-files/4927323/4927323-uhd_2732_1440_30fps.mp4")
      }
    } catch (error) {
      setVideoUrl("https://videos.pexels.com/video-files/4927323/4927323-uhd_2732_1440_30fps.mp4")
    }
  }

  const fetchMemberStats = async () => {
    try {
      const response = await fetch("/api/member-stats")
      const data = await response.json()
      if (response.ok && data.success) {
        setTotalMembers(data.total || 1)
        setMonthlyMembers(data.monthly || 0)
      } else {
        setTotalMembers(1)
        setMonthlyMembers(0)
      }
    } catch (error) {
      setTotalMembers(1)
      setMonthlyMembers(0)
    }
  }

  const handleJoin = async () => {
    triggerHaptic('impact')
    
    if (email) {
      router.push(`/auth/register?email=${encodeURIComponent(email)}`)
    } else {
      router.push("/auth/register")
    }
  }

  return (
    <div className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background Video with subtle blur */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        <video
          autoPlay loop muted playsInline
          className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 object-cover scale-[1.05]"
        >
          <source src={videoUrl || "https://videos.pexels.com/video-files/4927323/4927323-uhd_2732_1440_30fps.mp4"} type="video/mp4" />
        </video>
        {/* Modern high-contrast overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/20 to-slate-50 dark:to-slate-950 transition-colors duration-700" />
      </div>

      {/* Hero Content */}
      <div className="relative z-20 max-w-6xl mx-auto px-6 py-20 text-center">
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
          {/* Badge Row */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            {mounted && totalMembers !== null && (
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 px-5 py-2 rounded-2xl flex items-center gap-2 shadow-2xl">
                <Users className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-black uppercase tracking-widest text-white italic">{totalMembers} Members Joined</span>
              </div>
            )}
            <div className="bg-blue-600/90 backdrop-blur-xl border border-blue-400/30 px-5 py-2 rounded-2xl flex items-center gap-2 shadow-2xl shadow-blue-600/20 animate-pulse">
              <Sparkles className="w-4 h-4 text-white" />
              <span className="text-xs font-black uppercase tracking-widest text-white italic">First 250 Lifetime Free</span>
            </div>
          </div>

          {/* Typography Overhaul */}
          <div className="space-y-4">
            <h1 className="text-5xl md:text-8xl lg:text-9xl font-black text-white uppercase tracking-tighter italic leading-[0.85] drop-shadow-2xl">
              Find your<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-300 to-teal-400 pb-1 px-2 -mx-2">Perfect Wave</span>
            </h1>
            <p className="max-w-2xl mx-auto text-lg md:text-xl font-bold text-white/80 uppercase tracking-tight italic drop-shadow-lg">
              The ultimate platform for surf & kite adventurers. Track forecasts, connect with crews, and discover secret spots.
            </p>
          </div>

          {/* Premium Signup Area */}
          <div className="max-w-2xl mx-auto">
            {emailSent ? (
              <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl p-10 rounded-[3rem] shadow-2xl border border-white/20 space-y-4">
                <div className="w-16 h-16 bg-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-blue-600/30">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-3xl font-black uppercase tracking-tight italic text-slate-900 dark:text-white">Check your waves!</h3>
                <p className="font-bold text-slate-500 uppercase tracking-tight">We've sent a magic link to {email}</p>
                <Button onClick={() => setEmailSent(false)} variant="ghost" className="mt-4 font-black uppercase tracking-widest text-[10px] text-blue-600">Use another email</Button>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-3 p-3 bg-white/10 backdrop-blur-2xl rounded-[2.5rem] border border-white/20 shadow-2xl">
                <Input
                  type="email"
                  placeholder="Drop your email here..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-16 px-8 rounded-2xl bg-white/90 dark:bg-slate-900/90 border-none font-black uppercase tracking-widest text-xs focus-visible:ring-blue-500 shadow-inner"
                />
                <Button
                  onClick={handleJoin}
                  disabled={loading}
                  className="h-16 px-10 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-600/30 group transition-all"
                >
                  {loading ? "Launching..." : "Join the Crew"}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            )}
            {error && <p className="mt-4 text-red-400 font-black uppercase tracking-widest text-[10px] italic bg-red-400/10 py-2 rounded-xl border border-red-400/20">{error}</p>}
            {!emailSent && (
              <div className="mt-6 space-y-4">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/50 italic">Free to join • Worldwide Community • Live Data</p>
                <p className="text-xs text-white/70 font-bold italic uppercase tracking-widest">
                  Already have an account?{" "}
                  <button
                    onClick={() => {
                      triggerHaptic("impact")
                      router.push("/auth/login")
                    }}
                    className="text-blue-400 hover:text-blue-300 hover:underline underline-offset-4 ml-1 not-italic cursor-pointer transition-colors"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom fade out to grid */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-50 dark:from-slate-950 to-transparent z-10" />
    </div>
  )
}
